import { useQuery } from '@tanstack/react-query'
import { getDailyScheduleOptions } from '@/api/main/@tanstack/react-query.gen'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { sortBy } from 'lodash-es'
import { DailyScheduleResponse } from '@/api/main/types.gen'
import { useMemo } from 'react'

// Configure dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)

export interface CurrentShift {
  id: number
  vehicle_id: string
  driver: string
  start: string
  end: string
  startDateTime: dayjs.Dayjs
  endDateTime: dayjs.Dayjs
  status: 'running' | 'upcoming' | 'completed'
}

const createShiftSelector = (currentTime: dayjs.Dayjs, sixAM: dayjs.Dayjs) => {
  return (data: DailyScheduleResponse | undefined): CurrentShift[] => {
    if (!data) return []

    const shifts = data.vehicle_shifts
      .flatMap(vs =>
        vs.shifts.map(shift => ({
          id: shift.id,
          vehicle_id: vs.vehicle_id,
          driver: shift.driver?.name || 'N/A',
          start: shift.interval.starting_from,
          end: shift.interval.ending_at,
          startDateTime: dayjs.utc(shift.interval.starting_from).tz('Europe/Prague'),
          endDateTime: dayjs.utc(shift.interval.ending_at).tz('Europe/Prague'),
        }))
      )
      .map(shift => {
        const isRunning = shift.startDateTime.isBefore(currentTime) && shift.endDateTime.isAfter(currentTime)
        const isStartingSoon = shift.startDateTime.isAfter(currentTime) && shift.startDateTime.isBefore(sixAM)
        let status: 'running' | 'upcoming' | 'completed' = 'upcoming'
        if (isRunning) {
          status = 'running'
        } else if (isStartingSoon) {
          status = 'upcoming'
        }
        return { ...shift, status }
      })

    return sortBy(shifts, [
      shift => (shift.status === 'running' ? 0 : 1), // Running shifts first
      shift => shift.startDateTime.valueOf(), // Then by start time
    ])
  }
}

export const useCurrentShifts = () => {
  const selectShifts = useMemo(() => {
    const now = dayjs()
    const currentTime = now
    const sixAM = now.hour(6).minute(0).second(0)
    return createShiftSelector(currentTime, sixAM)
  }, [])

  return useQuery({
    ...getDailyScheduleOptions({ path: { schedule_date: dayjs().format('YYYY-MM-DD') } }),
    select: selectShifts,
  })
}
